import { AxiosResponse } from 'axios';
import { Actions } from 'vuex-smart-module';
import { ClientsService } from '@/modules/clients/services';
import { Filters, DataPilot } from '@/modules/data-pilot/types';
import { DataPilotService } from '../services';
import DataPilotGetters from './getters';
import DataPilotMutations from './mutations';
import { DataPilotState } from './state';

const dataPilotService = new DataPilotService();
const clientsService = new ClientsService();

export default class DataPilotActions extends Actions<
  DataPilotState,
  DataPilotGetters,
  DataPilotMutations,
  // eslint-disable-next-line no-use-before-define
  DataPilotActions
> {
  async get_client_events() {
    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.table_options;

      const payload = {
        filters: this.state.filters,
        filters_exist: true,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      this.mutations.TOGGLE_LOADING(true);
      const { data, meta } = await dataPilotService.get_client_events(payload);

      this.mutations.SET_CLIENT_EVENTS(data.items);
      this.mutations.SET_METRICS(data.metrics);
      this.mutations.SET_EVENTS(data.events);

      this.mutations.UPD_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.TOGGLE_LOADING(false);
    }
  }

  async get_filters() {
    try {
      const { data } = await dataPilotService.get_filters();

      this.mutations.SET_TAGS(data.tags);
      this.mutations.SET_MODELS(data.models);
      this.mutations.SET_EVENTS(data.events);
      this.mutations.SET_RESPONSIBLE_CLIENTS(data.users);
      this.mutations.SET_EVENT_STATUSES(data.event_statuses);
      this.mutations.SET_RESPONSIBLE_EVENTS(data.users);
      this.mutations.SET_PRIORITIES(data.priorities);
      this.mutations.SET_CLIENT_STATUSES(data.client_statuses);
      this.mutations.SET_PRODUCT_CATEGORIES(data.product_categories);
      this.mutations.SET_MARKS(data.marks);
      this.mutations.SET_OKVEDS(data.okveds);
      this.mutations.SET_REGIONS(data.regions);
      this.mutations.SET_CITIES(data.cities);
      this.mutations.SET_CLIENT_CATEGORIES(data.client_categories);
      this.mutations.SET_TAX_SYSTEM(data.tax_systems);
    } catch (error) {
      console.log(error);
    }
  }

  async get_devices() {
    try {
      const payload = {
        ...this.state.filters,
      };

      const { data } = await dataPilotService.get_devices(payload);

      this.mutations.SET_DEVICES(data.devices);
      this.mutations.SET_COUNT_CLIENTS(data.count_clients);
      this.mutations.SET_EVENTS(data.events);
    } catch (error) {
      console.log(error);
    }
  }

  async get_client(payload: { id: number, filters: Filters | null }) {
    try {
      const { data } = await dataPilotService.get_client(payload);

      this.mutations.SET_CLIENT(data);
    } catch (error) {
      console.log(error);
    }
  }

  async update_responsible_client(payload: { id: number, user_id: number | null }) {
    try {
      const { data } = await dataPilotService.update_responsible_client(payload);

      this.mutations.SET_RESPONSIBLE_CLIENT(data);
    } catch (error) {
      console.log(error);
    }
  }

  async get_client_by_id(id: string | number) {
    try {
      const { data } = await clientsService.get_client_by_id(id);

      this.mutations.SET_CURRENT_CLIENT_CONTACTS(data.client_contacts ?? []);
    } catch (error) {
      console.error(error);
      return (error as AxiosResponse).status;
    }
  }

  async create_mark(payload: { name: string | null, client_id: number }) {
    try {
      const { data } = await dataPilotService.create_mark(payload);

      this.mutations.SET_MARK(data);
      this.mutations.SET_CLIENT_MARK(data);
    } catch (error) {
      console.log(error);
    }
  }

  async delete_mark(payload: { id: number, client_id: number }) {
    try {
      const { data } = await dataPilotService.delete_mark(payload);

      this.mutations.DELETE_MARK(payload.id);

      if (data.all_mark_deleted) {
        this.mutations.DELETE_MARK_IN_FILTER(payload.id);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async update_mass_client(payload: DataPilot.MassUpdateRequest): Promise<number> {
    try {
      const { data } = await dataPilotService.update_mass_client(payload);

      return data.count;
    } catch (error) {
      console.log(error);

      throw error;
    }
  }

  async update_mass_events(payload: DataPilot.MassUpdateRequest): Promise<number> {
    try {
      const { data } = await dataPilotService.update_mass_events(payload);

      return data.count;
    } catch (error) {
      console.log(error);

      throw error;
    }
  }

  async get_count_clients(payload: DataPilot.MassUpdateFilters): Promise<number> {
    try {
      const { data } = await dataPilotService.get_count_clients(payload);

      return data.count;
    } catch (error) {
      console.log(error);

      throw error;
    }
  }

  async get_count_events(payload: DataPilot.MassUpdateFilters): Promise<number> {
    try {
      const { data } = await dataPilotService.get_count_events(payload);

      return data.count;
    } catch (error) {
      console.log(error);

      throw error;
    }
  }
}
